import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { leadsEndpoints } from "@/config/endpoints";

export interface PhoneCheckResult {
  /** `false` when the number can't be parsed at all (create would 400). */
  valid: boolean;
  /** A lead with this number already exists in the tenant → save will 409. */
  exists: boolean;
  phoneNormalized: string | null;
  /** The conflicting lead is soft-deleted (Trash). */
  inTrash: boolean;
  isArchived: boolean;
  /**
   * Replace-on-create: the conflict is in Trash or Archived, so a CREATE
   * will permanently delete the old lead and save this one as a completely
   * fresh lead (all new-lead automations fire). Does NOT apply to edits —
   * changing an existing lead's number onto this one still 409s.
   * Optional so an older server (which still 409s these) reads as `false`.
   */
  willReplace?: boolean;
  /** The conflicting lead is inside the caller's read scope. */
  visible: boolean;
  /** Only populated when `visible` — the server withholds it otherwise. */
  lead: { id: number; name: string; createdAt: string } | null;
  /**
   * Who holds the existing lead (lead-level holders, else its deal holders;
   * active users only). The warning names THEM, never the lead. Absent on an
   * older server; empty for a trashed lead or one nobody holds.
   */
  managedBy?: PhoneConflictHolder[];
}

export interface PhoneConflictHolder {
  userId: number;
  name: string;
  roleName: string | null;
  isYou: boolean;
}

/**
 * Query-key root. Exported so lead create / update can invalidate it — a
 * cached "available" for a number that was just used would otherwise stay
 * warm long enough for the next form to trust it.
 */
export const PHONE_CHECK_KEY = "lead-phone-check";

/** Debounce so a 10-digit number costs one request, not ten. */
const DEBOUNCE_MS = 450;

/**
 * Minimum digits before we bother asking. Shorter inputs can't parse into a
 * valid number for any region, so a request would only ever answer
 * `valid:false` — which is noise while someone is halfway through typing.
 */
const MIN_DIGITS = 6;

/**
 * As-you-type duplicate probe for the lead phone field.
 *
 * Mirrors the server's save-time uniqueness rule (tenant-wide on E.164,
 * trashed rows included) so the "already exists for this tenant" 409 shows
 * up at the field instead of after a full submit. The server remains the
 * authority — this is an affordance, never the enforcement.
 *
 * Pass `excludeLeadId` on the edit form so a lead's own row isn't reported
 * as a conflict with itself.
 */
export function useCheckLeadPhone({
  phone,
  countryCode,
  excludeLeadId,
  enabled = true,
  purpose = "create",
}: {
  phone?: string | null;
  countryCode?: string | null;
  excludeLeadId?: number | string | null;
  enabled?: boolean;
  /**
   * "create" (Add Lead): a trashed/archived conflict does NOT block — the
   * server replaces it with a completely fresh lead on save. "edit": ANY
   * conflict blocks, mirroring `LeadUpdate`'s unconditional 409.
   */
  purpose?: "create" | "edit";
}) {
  // Send the value EXACTLY as typed (only trimmed) — the server parses it the
  // same way the save path does, and stripping characters here (a pasted `+`
  // and country code, say) would make the probe answer a different question
  // than the one the save will ask.
  const raw = String(phone ?? "").trim();
  const cc = String(countryCode ?? "").trim();

  // Debounce the *value*, not the request: both the field and the footer
  // Save button call this hook, and settling on the same value means they
  // share one query key (and therefore one network call).
  const [debounced, setDebounced] = useState(raw);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(raw), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [raw]);

  // Only trust an answer that describes what's in the box right now —
  // otherwise the stale result of the previous keystroke reads as a verdict
  // on the current number.
  const settled = debounced === raw;
  const longEnough = raw.replace(/[^\d]/g, "").length >= MIN_DIGITS;
  const shouldCheck = enabled && settled && longEnough;

  const excludeId =
    excludeLeadId != null && Number(excludeLeadId) > 0
      ? Number(excludeLeadId)
      : undefined;

  const query = useQuery({
    queryKey: [PHONE_CHECK_KEY, debounced, cc, excludeId ?? null],
    queryFn: async () => {
      const { data } = await axiosInstance.get<
        { status: string } & PhoneCheckResult
      >(`${leadsEndpoints}/check-phone`, {
        params: {
          phone: debounced,
          ...(cc ? { countryCode: cc } : {}),
          ...(excludeId ? { excludeLeadId: excludeId } : {}),
        },
      });
      return data;
    },
    enabled: shouldCheck,
    // The answer is a property of the tenant's lead base, which barely moves
    // during one form session — but keep it short enough that reopening the
    // form after creating the lead doesn't reuse a stale "available".
    staleTime: 30_000,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const result = shouldCheck ? query.data : undefined;

  // Only a CREATE replaces; an EDIT onto this number still 409s. An older
  // server never sends `willReplace`, and that server still 409s trashed
  // conflicts too — so defaulting to false is correct on both axes.
  const willReplace = purpose === "create" && Boolean(result?.willReplace);

  return {
    /** The save would 409 — a live conflicting lead exists. Blocks save. */
    isDuplicate: Boolean(result?.exists) && !willReplace,
    /**
     * The conflict is in Trash/Archived: saving is allowed and will
     * permanently delete the old lead, creating this one completely fresh.
     */
    willReplace,
    /** Server parsed the number and found nothing — safe to save. */
    isAvailable: Boolean(result && result.valid && !result.exists),
    /** Server could not parse the number (create would reject it). */
    isInvalid: Boolean(result && !result.valid),
    /** A check is in flight (or pending debounce) for the current input. */
    isChecking: (shouldCheck && query.isFetching) || (longEnough && !settled),
    result,
    /** Ready-to-render warning, or null when there's nothing to say. */
    message: buildMessage(result, willReplace),
  };
}

function buildMessage(
  result: PhoneCheckResult | undefined,
  willReplace: boolean,
): string | null {
  if (!result || !result.exists) return null;
  // Never the existing lead's name (2026-10-09): say WHO has it instead.
  if (willReplace) {
    const where = result.inTrash ? "Trash" : "Archived";
    return `This number belongs to a lead in ${where}. Saving will permanently delete that old lead and add this one as a completely fresh lead.`;
  }
  if (result.inTrash) {
    return "This number already exists on a lead in Trash. Restore that lead instead of re-using the number.";
  }
  const archived = result.isArchived ? " That lead is archived." : "";
  const holders = result.managedBy;
  if (!holders) {
    // Older server: no holder info to show.
    return result.visible
      ? `This number already exists on another lead.${archived}`
      : "This number already exists on a lead in your organisation that you don't have access to. Ask an admin to check.";
  }
  const others = holders.filter((h) => !h.isYou);
  if (others.length < holders.length) {
    return others.length > 0
      ? `This number is already one of your leads (also with ${holderList(others)}).${archived}`
      : `This number is already one of your leads.${archived}`;
  }
  if (others.length > 0) {
    return `This number is already managed by ${holderList(others)}.${archived}`;
  }
  return `This number already exists on a lead that isn't assigned to anyone.${archived} Ask an admin to check.`;
}

/** "Rekhila (Counsellor)", "A and B", "A, B and 2 others". */
function holderList(holders: PhoneConflictHolder[]): string {
  const label = (h: PhoneConflictHolder) =>
    h.roleName ? `${h.name} (${h.roleName})` : h.name;
  const shown = holders.slice(0, 2).map(label);
  const rest = holders.length - shown.length;
  if (rest > 0) return `${shown.join(", ")} and ${rest} other${rest === 1 ? "" : "s"}`;
  return shown.join(" and ");
}

export default useCheckLeadPhone;
